import { type Dispatch, memo, type SetStateAction, useState } from 'react';
import FooterControls from './FooterControls';
import { isBrowser, isMobileOnly, isTablet } from 'react-device-detect';
import Nav from '../nav/Nav';
import { MEControls } from './MEControls';
import styles from './Controls.module.css';
import { ControlHeader } from './ControlHeader';
import { MEGSControls } from './MEGSControls';
import { useIsLandscape } from '../../useEffects/useIsLandscape';
import type { DataRequested } from '../../types/app.types';
import type { IMEGSFilteringParameters } from '../../types/MEGS.types';

type Props = {
  setDataRequested: Dispatch<SetStateAction<DataRequested>>;
  dataRequested: DataRequested;
  navRequired: boolean;
  setNavRequired: Dispatch<SetStateAction<boolean>>;
  version: string;
  setContentToDisplay: Dispatch<SetStateAction<string>>;
  setPageMode: Dispatch<SetStateAction<boolean>>;
  handleVersionSwitch: () => void;
  MEGSFilteringParameters: Array<IMEGSFilteringParameters>;
  setMEGSFilteringParameters: Dispatch<
    SetStateAction<Array<IMEGSFilteringParameters>>
  >;
};

const Controls = memo(
  ({
    setDataRequested,
    dataRequested,
    navRequired,
    setNavRequired,
    version,
    setContentToDisplay,
    setPageMode,
    handleVersionSwitch,
    MEGSFilteringParameters,
    setMEGSFilteringParameters,
  }: Props) => {
    const isMegs = dataRequested.dataset === 'megs';

    // ESX layers the landing-page blue gradient over a faint country map for a
    // modern, cohesive look; MEGS keeps its original map-only styling. The
    // gradient is kept deep enough that white foreground text stays legible
    // across the whole panel.
    const backgroundImage = isMegs
      ? `url("/images/${dataRequested.country}-map.png")`
      : `linear-gradient(310deg, rgba(48, 96, 150, 0.95) 15%, rgba(0, 46, 77, 0.98) 82%), url("/images/${dataRequested.country}-map.png")`;
    const [incrementAmmount, setIncrementAmmount] = useState({
      incrementBy: 1,
    });

    const isLandscape = useIsLandscape();

    return (
      <>
        {isBrowser || isTablet ? (
          <Nav
            navRequired={navRequired}
            setNavRequired={setNavRequired}
            navWidth={isTablet ? '294px' : '359px'}
            version={version}
            handleVersionSwitch={handleVersionSwitch}
            setContentToDisplay={setContentToDisplay}
            setPageMode={setPageMode}
            country={dataRequested.country}
            dataset={dataRequested.dataset}
            setDataRequested={setDataRequested}
          />
        ) : (
          ''
        )}

        <div
          className={styles.controlWrapper}
          style={
            navRequired && (isBrowser || isTablet)
              ? {
                  overflowY: 'hidden',
                  paddingTop: '420px',
                }
              : {
                  display:
                    isLandscape && isMegs && isMobileOnly ? 'none' : 'block',
                }
          }
        >
          <div
            className={styles.controlContainer}
            style={{
              backgroundImage,
              height: !isMegs && isMobileOnly ? '300px' : '100%',
            }}
          >
            <ControlHeader
              isMegs={isMegs}
              setNavRequired={setNavRequired}
              handleVersionSwitch={handleVersionSwitch}
              dataRequested={dataRequested}
              version={version}
            />
            {isMegs ? (
              <MEGSControls
                MEGSFilteringParameters={MEGSFilteringParameters}
                setMEGSFilteringParameters={setMEGSFilteringParameters}
              />
            ) : (
              <>
                <MEControls incrementAmmount={incrementAmmount} />
                <FooterControls
                  setDataRequested={setDataRequested}
                  dataRequested={dataRequested}
                  incrementAmmount={incrementAmmount}
                  setIncrementAmmount={setIncrementAmmount}
                />
              </>
            )}
          </div>
        </div>
      </>
    );
  }
);

// Controls.whyDidYouRender = {
//   logOnDifferentValues: true,
//   customName: 'Menu'
// }

export default Controls;
